/**
 * Pure helpers for personal-baseline deviations. A metric's rolling 30-day
 * baseline (mean/sd/n, computed server-side) lets a reading be scored against
 * the user's own normal instead of a population range. All pure; unit-tested.
 */

import { fmtNumber } from './format.js';

/** Rolling trailing-30d baseline for a metric, as returned by /api/baselines. */
export interface Baseline {
  mean: number;
  sd: number;
  n: number;
  date: string;
}

/**
 * Which direction of deviation is favourable, so a z-score can be coloured.
 *  - `higher-worse`  resting HR, weight-when-cutting: above baseline = warn
 *  - `higher-better` HRV, VO₂ max: above baseline = good
 *  - `neutral`       weight in general: never coloured, only reported
 */
export type DeviationDirection = 'higher-worse' | 'higher-better' | 'neutral';

export type DeviationTone = 'good' | 'neutral' | 'warn';

export interface Deviation {
  /** Standard score (value − mean) / sd; 0 when the baseline is unusable. */
  z: number;
  /** Human label of the raw difference, e.g. "+4 vs 30d avg"; "—" if unusable. */
  label: string;
}

/**
 * Score a reading against its rolling baseline. A degenerate baseline (sd ≤ 0)
 * or a non-finite input yields z = 0 and an em-dash label — we never render a
 * confident "+0" for a deviation we can't actually stand behind. The label
 * carries the raw difference (not the z), in the metric's own units.
 */
export function deviation(value: number, baseline: Baseline): Deviation {
  const { mean, sd } = baseline;
  if (
    !Number.isFinite(value) ||
    !Number.isFinite(mean) ||
    !Number.isFinite(sd) ||
    sd <= 0
  ) {
    return { z: 0, label: '—' };
  }
  const z = (value - mean) / sd;
  const rounded = Number((value - mean).toFixed(1));
  if (rounded === 0) return { z, label: 'on par with 30d avg' };
  const sign = rounded > 0 ? '+' : '';
  return { z, label: `${sign}${fmtNumber(rounded)} vs 30d avg` };
}

/**
 * Colour a deviation. Within ±1 sd everything reads neutral (normal variation);
 * beyond that the tone follows `direction`. A neutral direction is always
 * neutral, however large the swing.
 */
export function deviationTone(z: number, direction: DeviationDirection): DeviationTone {
  if (!Number.isFinite(z) || Math.abs(z) < 1 || direction === 'neutral') return 'neutral';
  const above = z > 0;
  if (direction === 'higher-worse') return above ? 'warn' : 'good';
  return above ? 'good' : 'warn'; // higher-better
}
